import { Autocomplete, Checkbox, CircularProgress, InputLabel, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  addValueToTemplateScope,
  getScopeValuesForTemplate,
  removeValueFromTemplateScope,
  searchScopeValues,
} from '../../../../services/scope.service';
import { KeyValuePair } from '../../../../types/KeyValuePair';

interface TemplateScopeSelectorProps {
  templateId: string;
  scopeName: string;
}

let timeout: ReturnType<typeof setTimeout>;

const TemplateScopeSelector = (props: TemplateScopeSelectorProps) => {
  const [options, setOptions] = useState<readonly KeyValuePair[]>([]);
  const [selectedValues, setSelectedValues] = useState<KeyValuePair[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsLoading(true);
    getScopeValuesForTemplate(props.templateId, props.scopeName)
      .then((x) => {
        if (x.status === 200) {
          setSelectedValues(x.data);
        }
      })
      .finally(() => setIsLoading(false));
  }, [props.templateId, props.scopeName]);

  const onValueChange = async (event: any, newValue: KeyValuePair | null) => {
    if (newValue) {
      setIsLoading(true);
      await addValueToTemplateScope(props.templateId, props.scopeName, newValue.key)
        .then((x) => {
          if (x.status === 200) {
            setSelectedValues(x.data);
            // Focus on the text field again
            if (textFieldRef.current) {
              textFieldRef.current.focus();
            }
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const onInputChange = (event: any, value: string) => {
    setTimerForAutocomplete(value);
  };

  const onCheckBoxChange = async (item: KeyValuePair) => {
    setIsLoading(true);
    await removeValueFromTemplateScope(props.templateId, props.scopeName, item.key)
      .then((x) => {
        if (x.status === 200) {
          setSelectedValues(x.data);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const getSearchOptions = async (input: string) => {
    if (input.length < 1) {
      return;
    }

    await searchScopeValues(props.scopeName, input).then((response) => {
      if (!response?.data) {
        console.error('No results', response);
        return [];
      }
      setOptions(response.data);
    });
  };

  const setTimerForAutocomplete = (input: string) => {
    timeout && clearTimeout(timeout);
    timeout = setTimeout(() => getSearchOptions(input), 100);
  };

  return (
    <>
      <Autocomplete
        filterOptions={(x) => x} // disable default filtering, because we filter in the backend
        id='serialNumber'
        options={options}
        renderOption={(props, option) => (
          <span {...props} key={option.key}>
            {option.value}
          </span>
        )}
        getOptionLabel={(option) => option.value ?? ''}
        noOptionsText='No options'
        clearOnBlur={true}
        blurOnSelect={true}
        includeInputInList
        renderInput={(params) => (
          <TextField
            variant='standard'
            {...params}
            label={'Search...'}
            inputRef={textFieldRef} // Assign ref to the TextField component
          />
        )}
        onChange={onValueChange}
        onInputChange={onInputChange}
        value={null} // Reset value after selection
      />
      <div style={{ height: '1em', paddingTop: '0.5em', marginBottom: '0.5em', paddingLeft: '0.75em' }}>
        {isLoading && <CircularProgress style={{ height: '1em', width: '1em' }} />}
      </div>
      <div>
        {!selectedValues.length && <span className='txt-muted'>No values assigned</span>}
        {selectedValues?.map((x, idx) => (
          <InputLabel key={idx} style={{ width: '100%', cursor: 'pointer' }}>
            <Checkbox checked={true} onChange={() => onCheckBoxChange(x)} />
            {x.value}
          </InputLabel>
        ))}
      </div>
    </>
  );
};

export default TemplateScopeSelector;
